/**
 * Recurring events, web side (YAR-443 — Option B of the recurring-events
 * decision, docs/superpowers/specs/2026-09-10-recurring-events-decision.md).
 *
 * The rule lives on the review item (`review_items.recurrence`) and, as a
 * display copy, on each materialised occurrence (`events.recurrence`). Only
 * `until` is editable from the web; day and frequency are read-only.
 *
 * All date arithmetic here runs on `YYYY-MM-DD` keys in UTC, so a DST switch
 * can never shift a day.
 */
import type { Json } from '@repo/supabase-client';
import { RECURRENCE_COPY } from '@/constants/recurrenceCopy';

export const WEEKDAY_CODES = [
  'MO',
  'TU',
  'WE',
  'TH',
  'FR',
  'SA',
  'SU',
] as const;
export type WeekdayCode = (typeof WEEKDAY_CODES)[number];

export interface Recurrence {
  freq: 'weekly';
  interval: number;
  by_day: WeekdayCode[];
  /** Inclusive last day, `YYYY-MM-DD`. null = no end stated. */
  until: string | null;
}

/** The count shown on the card stops here ("52+ sessions"). */
export const SESSION_COUNT_CAP = 52;

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Narrow a jsonb value to a rule this UI knows how to show. Anything else —
 * null, another freq, an empty day list, a malformed `until` — reads as "not
 * recurring", so the card renders exactly as before.
 */
export function parseRecurrence(value: unknown): Recurrence | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (v.freq !== 'weekly') return null;
  if (!Array.isArray(v.by_day) || v.by_day.length === 0) return null;
  const days = v.by_day.filter((d): d is WeekdayCode =>
    (WEEKDAY_CODES as readonly unknown[]).includes(d),
  );
  if (days.length !== v.by_day.length) return null;
  const interval =
    typeof v.interval === 'number' &&
    Number.isInteger(v.interval) &&
    v.interval >= 1
      ? v.interval
      : 1;
  const until =
    typeof v.until === 'string' && DATE_KEY.test(v.until) ? v.until : null;
  // Week order, not extraction order: "Monday and Wednesday", always.
  const byDay = WEEKDAY_CODES.filter((c) => days.includes(c));
  return { freq: 'weekly', interval, by_day: byDay, until };
}

// ── Date-key helpers ────────────────────────────────────────────────

function keyToUtc(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function utcToKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(key: string, days: number): string {
  const d = keyToUtc(key);
  d.setUTCDate(d.getUTCDate() + days);
  return utcToKey(d);
}

/** Monday = 0 … Sunday = 6 (RFC 5545 default week start). */
function mondayIndex(key: string): number {
  return (keyToUtc(key).getUTCDay() + 6) % 7;
}

/** The latest end date allowed: the same calendar day one year later. */
export function maxUntilFor(startKey: string): string {
  const d = keyToUtc(startKey);
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return utcToKey(d);
}

// ── Counting ────────────────────────────────────────────────────────

/** The Monday of the week `key` falls in. */
function mondayOf(key: string): string {
  return addDays(key, -mondayIndex(key));
}

function daysBetween(from: string, to: string): number {
  return Math.round(
    (keyToUtc(to).getTime() - keyToUtc(from).getTime()) / 86400000,
  );
}

/**
 * How many sessions the rule produces from `startKey` through `until`,
 * inclusive — counted EXACTLY as the backend expands them at approval
 * (`occurrenceDays` in `functions/_shared/recurrence.ts`):
 *
 *   - the start date, always (even on a day outside `by_day`);
 *   - then every `by_day` date after it, through `until`, in weeks where
 *     `weekIndex % interval === 0` — weeks counted Monday-based from the
 *     START's week (interval 2 = every other week);
 *   - `until` before the start → the start alone.
 *
 * The backend stops at {@link SESSION_COUNT_CAP} occurrences; when the rule
 * would produce more, this reports `capped` so the card says "52+".
 * null = no end date (only the first session is added).
 */
export function countSessions(
  startKey: string,
  rule: Recurrence,
): { count: number; capped: boolean } | null {
  if (!rule.until) return null;
  let count = 1; // the start itself
  if (rule.until <= startKey) return { count, capped: false };
  const startWeek = mondayOf(startKey);
  for (let d = addDays(startKey, 1); d <= rule.until; d = addDays(d, 1)) {
    if (!rule.by_day.includes(WEEKDAY_CODES[mondayIndex(d)])) continue;
    const weekIndex = daysBetween(startWeek, mondayOf(d)) / 7;
    if (weekIndex % rule.interval !== 0) continue;
    count += 1;
    if (count > SESSION_COUNT_CAP) {
      return { count: SESSION_COUNT_CAP, capped: true };
    }
  }
  return { count, capped: false };
}

// ── Formatting ──────────────────────────────────────────────────────

/** A Monday, used as the anchor to ask Intl for localized weekday names. */
const MONDAY_ANCHOR = Date.UTC(2024, 0, 1);

export function weekdayName(code: WeekdayCode, locale = 'en-US'): string {
  const date = new Date(MONDAY_ANCHOR + WEEKDAY_CODES.indexOf(code) * 86400000);
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    timeZone: 'UTC',
  }).format(date);
}

/** "Tuesday", "Monday and Wednesday", "Monday, Wednesday, and Friday". */
export function formatWeekdays(days: WeekdayCode[], locale = 'en-US'): string {
  const names = days.map((d) => weekdayName(d, locale));
  return new Intl.ListFormat(locale, {
    style: 'long',
    type: 'conjunction',
  }).format(names);
}

/** "every Tuesday" / "every other Tuesday" / "every 3 weeks on Tuesday". */
export function formatPattern(rule: Recurrence, locale = 'en-US'): string {
  const days = formatWeekdays(rule.by_day, locale);
  if (rule.interval === 1) return RECURRENCE_COPY.everyWeek(days);
  if (rule.interval === 2) return RECURRENCE_COPY.everyOtherWeek(days);
  return RECURRENCE_COPY.everyNWeeks(rule.interval, days);
}

/** "Dec 18"; the year is added only when it differs from the start's. */
export function formatUntil(
  untilKey: string,
  startKey: string,
  locale = 'en-US',
): string {
  const sameYear = untilKey.slice(0, 4) === startKey.slice(0, 4);
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  }).format(keyToUtc(untilKey));
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export type RecurrenceSummary =
  | { kind: 'open'; line: string; hint: string }
  | { kind: 'bounded'; line: string; count: number; capped: boolean };

/**
 * The card's one-line summary.
 *   open:    "Repeats every Tuesday" (+ the first-session-only hint)
 *   bounded: "Every Tuesday · through Dec 18 · 12 sessions"
 */
export function summarizeRecurrence(
  rule: Recurrence,
  startKey: string,
  locale = 'en-US',
): RecurrenceSummary {
  const pattern = formatPattern(rule, locale);
  const counted = rule.until ? countSessions(startKey, rule) : null;
  if (!rule.until || !counted) {
    return {
      kind: 'open',
      line: RECURRENCE_COPY.repeatsOpenEnded(pattern),
      hint: RECURRENCE_COPY.firstSessionOnlyHint(
        formatUntil(startKey, startKey, locale),
      ),
    };
  }
  const line = [
    capitalize(pattern),
    RECURRENCE_COPY.through(formatUntil(rule.until, startKey, locale)),
    RECURRENCE_COPY.sessions(counted.count, counted.capped),
  ].join(RECURRENCE_COPY.separator);
  return {
    kind: 'bounded',
    line,
    count: counted.count,
    capped: counted.capped,
  };
}

/**
 * The label on a committed event of a series (Schedule / Today cards, event
 * detail), or null for a one-off event.
 *
 *   no end date (one occurrence was added): "Repeats every Tuesday"
 *   with an end date: "Repeats weekly" / "Repeats every other week" /
 *                     "Repeats every 3 weeks"
 *   series_id but no readable rule: "Repeats weekly"
 */
export function seriesLabel(
  event: { series_id?: string | null; recurrence?: unknown },
  locale = 'en-US',
): string | null {
  if (!event.series_id) return null;
  const rule = parseRecurrence(event.recurrence);
  if (!rule) return RECURRENCE_COPY.repeatsWeekly;
  if (!rule.until) {
    return RECURRENCE_COPY.repeatsOpenEnded(formatPattern(rule, locale));
  }
  if (rule.interval === 1) return RECURRENCE_COPY.repeatsWeekly;
  if (rule.interval === 2) return RECURRENCE_COPY.repeatsEveryOtherWeek;
  return RECURRENCE_COPY.repeatsEveryNWeeks(rule.interval);
}

/**
 * Whether "Delete this and following" can remove more than this occurrence.
 * A rule with no end date was approved as ONE event, so there is nothing
 * after it to delete.
 */
export function seriesMayHaveFollowing(event: {
  series_id?: string | null;
  recurrence?: unknown;
}): boolean {
  if (!event.series_id) return false;
  const rule = parseRecurrence(event.recurrence);
  return !rule || rule.until !== null;
}

// ── Validation ──────────────────────────────────────────────────────

export type UntilError = 'beforeStart' | 'tooFar';

/** null = valid. The end date may equal the start (one session). */
export function validateUntil(
  untilKey: string,
  startKey: string,
): UntilError | null {
  if (untilKey < startKey) return 'beforeStart';
  if (untilKey > maxUntilFor(startKey)) return 'tooFar';
  return null;
}

export function untilErrorMessage(error: UntilError): string {
  return error === 'beforeStart'
    ? RECURRENCE_COPY.endDateBeforeStart
    : RECURRENCE_COPY.endDateTooFar;
}

/** The rule with a new end date — the only field the web ever changes. */
export function withUntil(rule: Recurrence, until: string | null): Recurrence {
  return { ...rule, until };
}

/** The rule as a jsonb value for a `review_items` write. */
export function recurrenceToJson(rule: Recurrence): Json {
  return {
    freq: rule.freq,
    interval: rule.interval,
    by_day: [...rule.by_day],
    until: rule.until,
  };
}
